import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import SearchIcon from "../../media/SearchIcon";
import { useTheme } from "../../contexts/ThemeContext";
import fullLogo from "../../media/full_logo.svg";
import {
    Carousel,
    CarouselContent,
    CarouselItem,
    CarouselPrevious,
    CarouselNext,
  } from "../ui/Carousel";
  import {
    Card,
    CardHeader,
    CardTitle,
    CardDescription,
    CardContent,
    CardFooter,
  } from "../ui/Card";
import { Link } from "react-router-dom";
import useBlogStorage from "../hooks/useBlogStorage"

interface BlogSummary {
  filename: string;
  title: string;
  image?: string;
  summary: string;
}

const ArticlePage = ({ isPageExpanded }: { isPageExpanded: boolean }) => {
    const { theme } = useTheme();
    const { id } = useParams();
    const [article, setArticle] = useState<string>("");
    const [error, setError] = useState<boolean>(false);
    const [date, setDate] = useState<string>("");
    const [readTime, setReadTime] = useState<string>("");

    // Related blogs state for carousel
    const [relatedBlogs, setRelatedBlogs] = useState<BlogSummary[]>([]);
    const [relatedLoading, setRelatedLoading] = useState<boolean>(true);
    const [relatedError, setRelatedError] = useState<boolean>(false);

    const { listMarkdowns: listMarkdownFiles, fetchMarkdown: fetchMarkdownFile } = useBlogStorage();

    // Helper to parse markdown contents (extract title, first image, summary)
    const parseMarkdown = (md: string): { title: string; image?: string; summary: string } => {
        let title = "";
        let image;
        let summary = "";

        const titleMatch = md.match(/^#\s+(.*)/m);
        if (titleMatch) {
            title = titleMatch[1];
        }

        const imageMatch = md.match(/!\[[^\]]*\]\((.*?)\)/);
        if (imageMatch) {
            image = imageMatch[1];
        }

        const paragraphs = md
            .split(/\r?\n\r?\n/)
            .map((p) => p.trim())
            .filter((p) => p.length > 0 && !p.startsWith("#"));
        if (paragraphs.length) {
            summary = paragraphs[0].replace(/(!?\[[^\]]*\]\([^)]*\))/g, "").slice(0, 250);
        }

        return { title, image, summary };
    };

    useEffect(() => {
        //hardcoded for now
        setDate("Oct 29, 2024");
        setReadTime("2 min read");

        const fetchArticle = async () => {
            try {
                const filename = id?.endsWith('.md') ? id : `${id}.md`;
                const markdown = await fetchMarkdownFile(filename);
                setArticle(markdown);
                setError(false);
            } catch (err) {
                console.error(`Error fetching article ${id}:`, err);
                setError(true);
                try {
                    const defaultText = await fetchMarkdownFile('default.md');
                    setArticle(defaultText);
                } catch (defaultErr) {
                    console.error("Error fetching default article:", defaultErr);
                    setArticle("# Article Not Found\n\nThe requested article could not be loaded, and the default article is also unavailable.");
                }
            }
        };
        fetchArticle();
    }, [id, fetchMarkdownFile]);

    // Fetch all / related blogs (DISCUSS: should this be all blogs or related blogs?) for carousel when article id changes
    useEffect(() => {
        const fetchRelatedBlogs = async () => {
            try {
                const filenames = await listMarkdownFiles();
                const currentFilename = id?.endsWith('.md') ? id : `${id}.md`;
                const validFilenames = filenames.filter((fn): fn is string => typeof fn === 'string');
                const promises = validFilenames
                    .filter((fn) => fn !== currentFilename) //exclude current article
                    .slice(0, 6) //limit to 6 related blogs
                    .map(async (filename) => {
                    try {
                        const md = await fetchMarkdownFile(filename);
                        const { title, image, summary } = parseMarkdown(md);
                        return { filename, title, image, summary } as BlogSummary;
                    } catch (err) {
                        console.error('Error fetching blog', filename, err);
                        return null;
                    }
                });
                const results = await Promise.all(promises);
                const blogs = results.filter((b): b is BlogSummary => Boolean(b));
                setRelatedBlogs(blogs);
                setRelatedLoading(false);
            } catch (err) {
                console.error(err);
                setRelatedError(true);
                setRelatedLoading(false);
            }
        };
        fetchRelatedBlogs();
    }, [id, listMarkdownFiles, fetchMarkdownFile]);

    // Scroll to top when article changes
    useEffect(() => {
        // Find the scrollable container and scroll it to top
        const scrollContainer = document.querySelector('.max-h-screen.overflow-y-auto');
        if (scrollContainer) {
            scrollContainer.scrollTop = 0;
        } else {
            // Fallback to window scroll
            window.scrollTo(0, 0);
        }
    }, [id]);

    const firstBreak = article.indexOf('\n\n\n');
    const articleHeader = firstBreak !== -1 ? article.slice(0, firstBreak) : article;
    const articleBody = firstBreak !== -1 ? article.slice(firstBreak + 2) : '';

    return (
        <div className="flex flex-col max-h-screen overflow-y-auto pb-16 items-center bg-gray-200 dark:bg-gray-900">
            <div className="w-full max-w-5xl flex justify-end bg-gray-200 dark:bg-gray-900 my-4 transition-all duration-300">
                <div className="w-full max-w-lg flex items-center bg-white dark:bg-gray-800 rounded-md px-3.5 py-3">
                    <div className="relative flex-1">
                        <input
                            type="text"
                            placeholder="Search for articles and blogs..."
                            className="w-full h-[32px] pl-10 pr-2 rounded-[3px] border border-[#C6C5C5] dark:border-[#4A5568] bg-transparent text-[#535353] dark:text-[#CEE0EF] text-[15px] focus:outline-none placeholder:text-[#C6C5C5] dark:placeholder:text-[#7A8690]"
                        />
                        <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none">
                            <SearchIcon theme={theme} />
                        </div>
                    </div>
                    <button className="ml-1 px-3 h-[32px] rounded-[3px] bg-accent1 hover:bg-accent1/90 text-white text-[15px] transition-colors font-semibold">
                        Search
                    </button>
                </div>
            </div>
            <div className="flex flex-col items-center w-full relative">
                <div className={`max-w-5xl px-16 pt-8 pb-12 bg-white dark:bg-[#2D3748] rounded shadow-lg relative ${error ? 'pointer-events-none select-none' : ''}`} style={error ? { filter: 'blur(10px)' } : {}}>
                    <ReactMarkdown
                        children={articleHeader}
                        remarkPlugins={[remarkGfm]}
                        components={{
                            h1: ({node, ...props}) => <h1 className="text-4xl font-bold text-accent1 dark:text-white mb-4" {...props}>{props.children}</h1>,
                            h2: ({node, ...props}) => <h2 className="text-2xl font-bold text-[#535353] mt-8 mb-4" {...props}>{props.children}</h2>,
                            img: ({node, ...props}) => (
                                <img className="w-full my-6" {...props} alt={props.alt} />
                            ),
                            p: ({node, ...props}) => <p className="mb-4 text-lg text-[#535353] font-normal" {...props} />, 
                        }}
                    />
                    <div className="flex items-center gap-2 my-6">
                        <div className="w-12 min-h-12 flex items-center justify-center rounded-[50%] border-2 border-[#C6C5C5] bg-white">
                            <img src={fullLogo} alt="Grid Insights Logo" className="w-full h-full p-1"/>
                        </div>
                        <div>
                            <div className="text-sm font-semibold text-[#0F172A] dark:text-white">@Grid Insights</div>
                            <div className="text-sm text-[#535353] dark:text-[#CEE0EF]">{date} · {readTime}</div>
                        </div>
                    </div>
                    <ReactMarkdown
                        children={articleBody}
                        remarkPlugins={[remarkGfm]}
                        components={{
                            h1: ({node, ...props}) => <h1 className="text-4xl font-bold text-accent1 dark:text-white mb-4" {...props}>{props.children}</h1>,
                            h2: ({node, ...props}) => <h2 className="text-2xl font-bold text-[#535353] mt-8 mb-4" {...props}>{props.children}</h2>,
                            img: ({node, ...props}) => (
                                <img className="w-full rounded my-6" {...props} alt={props.alt} />
                            ),
                            p: ({node, ...props}) => <p className="mb-4 text-lg text-[#535353] font-normal" {...props} />, 
                        }}
                    />
                </div>
                {error && (
                    <div className="absolute inset-0 flex justify-center items-center z-20">
                        <div className="bg-white dark:bg-[#2D3748] shadow-lg rounded-md text-center p-6 max-w-md">
                            <div className="font-semibold text-[#535353] dark:text-[#CEE0EF]">Oops! There was an error fetching this article.</div>
                            <div className="text-[#535353] dark:text-[#CEE0EF] mb-6">Please try again or return to the Blog page.</div>
                            <div className="flex justify-center gap-3">
                                <button onClick={() => window.location.reload()} className="px-2.5 py-1 rounded font-semibold text-[#535353] dark:text-[#CEE0EF] border-[1.5px] border-[#C6C5C5] dark:border-[#7A8690] hover:bg-gray-100/80 dark:hover:bg-gray-700/30 transition-colors">Refresh</button>
                                <a href="/blog" className="px-2.5 py-1 rounded font-semibold text-white bg-accent1 hover:bg-accent1/90 transition-colors text-center align-middle flex items-center justify-center">Blogs</a>
                            </div>
                        </div>
                    </div>
                )}
            </div>
            <div className={`${isPageExpanded ? 'w-[90dvw]' : 'w-[80dvw]'} px-24 pt-24`}>
                <Carousel opts={{ loop: true, align: "start" }}>
                    <CarouselContent>
                        {relatedLoading ? (
                            <CarouselItem className="flex justify-center px-4 basis-full">
                                <div className="text-center text-[#535353] dark:text-[#CEE0EF]">Loading related articles...</div>
                            </CarouselItem>
                        ) : relatedError ? (
                            <CarouselItem className="flex justify-center px-4 basis-full">
                                <div className="text-center text-red-500">Failed to load related articles.</div>
                            </CarouselItem>
                        ) : (
                            relatedBlogs.map((blog, idx) => (
                                <CarouselItem key={idx} className="flex justify-center basis-full md:basis-1/2 xl:basis-1/3 px-4">
                                    <Link to={`/blog/${blog.filename.replace('.md', '')}`} className="block w-full max-w-sm">
                                        <Card className="flex flex-col h-[420px] shadow-lg overflow-hidden border-0 bg-white dark:bg-[#2D3748]">
                                            <div className="h-[200px] overflow-hidden">
                                                {blog.image && (
                                                    <img src={blog.image} alt="blog" className="min-h-[200px]" />
                                                )}
                                            </div>
                                            <CardHeader className="pb-2">
                                                <CardTitle className="text-xl font-semibold text-accent1 dark:text-[#CEE0EF] line-clamp-2">
                                                    {blog.title}
                                                </CardTitle>
                                            </CardHeader>
                                            <CardContent className="flex-1 pb-2 overflow-hidden">
                                                <CardDescription className="text-sm text-[#535353] dark:text-[#CEE0EF] line-clamp-3">
                                                    {blog.summary}
                                                </CardDescription>
                                            </CardContent>
                                            <CardFooter className="flex items-center justify-between text-xs text-[#7A8690] dark:text-[#A0AEC0]">
                                                    <span>Oct 29, 2024</span>
                                                    <span>2 min read</span>
                                            </CardFooter>
                                        </Card>
                                    </Link>
                                </CarouselItem>
                            ))
                        )}
                    </CarouselContent>
                    <CarouselPrevious className="text-[#535353] dark:text-[#CEE0EF]"/>
                    <CarouselNext className="text-[#535353] dark:text-[#CEE0EF]"/>
                </Carousel>
            </div>
        </div>
    );
};

export default ArticlePage;
